export const USER_PREFERENCES_EVENT = 'sj:user-preferences-change';

export const DEFAULT_USER_PREFERENCES = {
  themeMode: 'system',
  fontSize: 'medium',
  fontFamily: 'default',
};

const STORAGE_KEYS = {
  themeMode: 'sj_theme_mode',
  fontSize: 'sj_font_size',
  fontFamily: 'sj_font_family',
};

const ALLOWED = {
  themeMode: ['system', 'light', 'dark'],
  fontSize: ['small', 'medium', 'large'],
  fontFamily: ['default', 'serif', 'mono'],
};

const FONT_SIZE_VARS = {
  small: { size: '15px', lineHeight: '1.65' },
  medium: { size: '16px', lineHeight: '1.75' },
  large: { size: '18px', lineHeight: '1.85' },
};

const FONT_FAMILY_VARS = {
  default: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  serif: "Georgia, 'Times New Roman', 'Songti SC', 'SimSun', serif",
  mono: "Menlo, Monaco, Consolas, 'Courier New', monospace",
};

function normalize(key, value) {
  return ALLOWED[key].includes(value) ? value : DEFAULT_USER_PREFERENCES[key];
}

export function getResolvedTheme(themeMode) {
  if (themeMode === 'light' || themeMode === 'dark') return themeMode;
  const prefersDark =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'dark' : 'light';
}

export function readUserPreferences() {
  try {
    return {
      themeMode: normalize('themeMode', localStorage.getItem(STORAGE_KEYS.themeMode)),
      fontSize: normalize('fontSize', localStorage.getItem(STORAGE_KEYS.fontSize)),
      fontFamily: normalize('fontFamily', localStorage.getItem(STORAGE_KEYS.fontFamily)),
    };
  } catch {
    return DEFAULT_USER_PREFERENCES;
  }
}

export function applyUserPreferences(preferences = readUserPreferences()) {
  const prefs = {
    themeMode: normalize('themeMode', preferences.themeMode),
    fontSize: normalize('fontSize', preferences.fontSize),
    fontFamily: normalize('fontFamily', preferences.fontFamily),
  };
  const root = document.documentElement;
  const fontSize = FONT_SIZE_VARS[prefs.fontSize];

  root.setAttribute('data-theme', getResolvedTheme(prefs.themeMode));
  root.setAttribute('data-font-size', prefs.fontSize);
  root.setAttribute('data-font-family', prefs.fontFamily);
  root.style.setProperty('--sj-readable-font-size', fontSize.size);
  root.style.setProperty('--sj-readable-line-height', fontSize.lineHeight);
  root.style.setProperty('--sj-readable-font-family', FONT_FAMILY_VARS[prefs.fontFamily]);

  return prefs;
}

export function saveUserPreferences(nextPreferences) {
  const current = readUserPreferences();
  const prefs = {
    themeMode: normalize('themeMode', nextPreferences.themeMode || current.themeMode),
    fontSize: normalize('fontSize', nextPreferences.fontSize || current.fontSize),
    fontFamily: normalize('fontFamily', nextPreferences.fontFamily || current.fontFamily),
  };

  localStorage.setItem(STORAGE_KEYS.themeMode, prefs.themeMode);
  localStorage.setItem(STORAGE_KEYS.fontSize, prefs.fontSize);
  localStorage.setItem(STORAGE_KEYS.fontFamily, prefs.fontFamily);
  applyUserPreferences(prefs);
  window.dispatchEvent(new CustomEvent(USER_PREFERENCES_EVENT, { detail: prefs }));
  return prefs;
}
